/**
 * InvoiceModal — create / edit a supplier invoice against a purchase order
 * (BLU-456). Ported from the heapsgood-ops InvoiceModal UX onto the IMS
 * design system.
 *
 * Line items are freehand-first: a row may soft-link to a PO line (audit
 * hint) but doesn't have to — suppliers bill in boxes, bundles, and SKUs we
 * don't stock, so reconciliation happens at the totals level.
 *
 * AI flow (create mode only): when the org has AI invoice ingestion enabled
 * and the attached file is a PDF, the modal uploads the file, then asks the
 * backend to analyze it and pre-fills header + lines from the proposal.
 * Variance flags render as an amber review list. The proposal is NEVER
 * auto-saved — the user reviews and saves like a manual entry.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  FileUp,
  Loader2,
  Paperclip,
  Plus,
  Sparkles,
  Trash2,
  X,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { trpc } from '@/lib/trpc';
import type { RouterOutputs } from '@/lib/trpc';
import { toast } from '@/hooks/use-toast';
import { useCurrencyFormatter } from '@/hooks/useCurrencyFormatter';
import { reportError } from '@/lib/logging';
import { readFileAsBase64 } from '@/lib/read-file-base64';
import type { PurchaseOrderItem } from '@/features/purchase-orders';

export type PoInvoice = RouterOutputs['poInvoices']['list'][number];
type InvoiceAnalysis = RouterOutputs['poInvoices']['analyze'];

interface LineRow {
  key: string;
  /** po_invoice_items.id when editing an existing row; null for new rows. */
  itemId: string | null;
  purchaseOrderItemId: string | null;
  sku: string;
  description: string;
  quantity: string;
  unitCost: string;
  taxRateId: string | null;
  taxRate: number;
  accountCode: string | null;
}

interface InvoiceModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orderId: string;
  poNumber: string;
  poItems: PurchaseOrderItem[];
  currency: string;
  /** null = create; non-null = edit. */
  invoice: PoInvoice | null;
  /** Fixed by the entry point on create; ignored on edit. */
  isDeposit: boolean;
  depositPercent: number | null;
  depositAmount: number | null;
  paymentTermsDays: number | null;
  onSaved: () => void;
}

const MAX_FILE_BYTES = 7 * 1024 * 1024;

const newKey = (): string => `ln-${Math.random().toString(36).slice(2, 10)}`;

const todayIso = (): string => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const addDays = (isoDate: string, days: number): string => {
  const date = new Date(`${isoDate}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
};

const toNumber = (value: string): number => {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

export function InvoiceModal({
  open,
  onOpenChange,
  orderId,
  poNumber,
  poItems,
  currency,
  invoice,
  isDeposit,
  depositPercent,
  depositAmount,
  paymentTermsDays,
  onSaved,
}: InvoiceModalProps) {
  const utils = trpc.useUtils();
  const { format } = useCurrencyFormatter();
  const isEdit = invoice !== null;
  const depositMode = isEdit ? invoice.is_deposit : isDeposit;

  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceNumberTouched, setInvoiceNumberTouched] = useState(false);
  const [invoiceDate, setInvoiceDate] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [dueDateTouched, setDueDateTouched] = useState(false);
  const [paymentTerms, setPaymentTerms] = useState('');
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState<LineRow[]>([]);
  /** Initial item ids on edit, so deletes can be diffed on save. */
  const initialItemIdsRef = useRef<Set<string>>(new Set());
  const [fileId, setFileId] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [proposal, setProposal] = useState<InvoiceAnalysis | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Seed all fields whenever the modal (re)opens — never carry state across
  // invocations (create after edit, deposit after balance, etc).
  useEffect(() => {
    if (!open) return;
    setProposal(null);
    setInvoiceNumberTouched(false);
    setIsSaving(false);
    if (invoice) {
      setInvoiceNumber(invoice.invoice_number);
      setInvoiceDate(invoice.invoice_date ?? '');
      setDueDate(invoice.due_date ?? '');
      setDueDateTouched(Boolean(invoice.due_date));
      setPaymentTerms(invoice.payment_terms ?? '');
      setNotes(invoice.notes ?? '');
      setFileId(invoice.file_id);
      setFileName(null);
      const rows = invoice.items.map((item) => ({
        key: newKey(),
        itemId: item.id,
        purchaseOrderItemId: item.purchase_order_item_id,
        sku: item.sku ?? '',
        description: item.description ?? '',
        quantity: String(item.quantity),
        unitCost: String(item.unit_cost),
        taxRateId: item.tax_rate_id,
        taxRate: Number(item.tax_rate ?? 0),
        accountCode: item.account_code,
      }));
      setItems(rows);
      initialItemIdsRef.current = new Set(invoice.items.map((item) => item.id));
    } else {
      const today = todayIso();
      setInvoiceNumber('');
      setInvoiceDate(today);
      setDueDate(paymentTermsDays ? addDays(today, paymentTermsDays) : '');
      setDueDateTouched(false);
      setPaymentTerms(paymentTermsDays ? `${paymentTermsDays} days` : '');
      setNotes('');
      setFileId(null);
      setFileName(null);
      initialItemIdsRef.current = new Set();
      if (isDeposit && (depositAmount ?? 0) > 0) {
        setItems([
          {
            key: newKey(),
            itemId: null,
            purchaseOrderItemId: null,
            sku: '',
            description: `Deposit${depositPercent ? ` (${depositPercent}%)` : ''} against ${poNumber}`,
            quantity: '1',
            unitCost: String(depositAmount),
            taxRateId: null,
            taxRate: 0,
            accountCode: null,
          },
        ]);
      } else {
        setItems([]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, invoice?.id]);

  // Re-derive due date from invoice date + payment terms until the user
  // overrides it (or an AI proposal supplies one).
  useEffect(() => {
    if (dueDateTouched || !invoiceDate || !paymentTermsDays) return;
    setDueDate(addDays(invoiceDate, paymentTermsDays));
  }, [invoiceDate, paymentTermsDays, dueDateTouched]);

  const aiStatusQuery = trpc.poInvoices.aiStatus.useQuery(undefined, {
    enabled: open,
    staleTime: 60_000,
  });
  const aiEnabled = aiStatusQuery.data?.enabled ?? false;

  const accountsQuery = trpc.xero.getAccounts.useQuery(
    { dropdownOnly: true },
    { enabled: open, staleTime: 5 * 60_000 }
  );
  const taxRatesQuery = trpc.xero.getTaxRates.useQuery(
    { activeOnly: true },
    { enabled: open, staleTime: 5 * 60_000 }
  );
  const accounts = accountsQuery.data ?? [];
  // Expense-side codes only — what a supplier bill gets coded with.
  const expenseTaxRates = useMemo(
    () =>
      (taxRatesQuery.data ?? []).filter((rate) =>
        ['INPUT', 'EXEMPTEXPENSES', 'BASEXCLUDED', 'INPUTTAXED', 'NONE'].includes(
          rate.xero_tax_type ?? ''
        )
      ),
    [taxRatesQuery.data]
  );

  const uploadFile = trpc.purchaseOrders.uploadPurchaseOrderFile.useMutation();
  const analyzeInvoice = trpc.poInvoices.analyze.useMutation();
  const createInvoice = trpc.poInvoices.create.useMutation();
  const updateInvoice = trpc.poInvoices.update.useMutation();
  const addItem = trpc.poInvoices.addItem.useMutation();
  const updateItem = trpc.poInvoices.updateItem.useMutation();
  const deleteItem = trpc.poInvoices.deleteItem.useMutation();

  const applyProposal = (analysis: InvoiceAnalysis) => {
    if (analysis.invoice_number) setInvoiceNumber(analysis.invoice_number);
    if (analysis.invoice_date) setInvoiceDate(analysis.invoice_date);
    if (analysis.due_date) {
      setDueDate(analysis.due_date);
      setDueDateTouched(true);
    }
    if (analysis.items.length > 0) {
      setItems(
        analysis.items.map((item) => ({
          key: newKey(),
          itemId: null,
          purchaseOrderItemId: item.matched_po_item_id,
          sku: item.sku ?? '',
          description: item.description,
          quantity: String(item.quantity),
          unitCost: String(item.unit_cost),
          taxRateId: null,
          taxRate: 0,
          accountCode: item.suggested_account_code,
        }))
      );
    }
  };

  const runAnalysis = (uploadedFileId: string) => {
    setProposal(null);
    analyzeInvoice.mutate(
      { purchaseOrderId: orderId, fileId: uploadedFileId },
      {
        onSuccess: (result) => {
          setProposal(result);
          applyProposal(result);
          toast({
            title: 'Invoice analyzed',
            description:
              result.variance_flags.length > 0
                ? `Pre-filled with ${result.variance_flags.length} item${result.variance_flags.length === 1 ? '' : 's'} to review below.`
                : 'Pre-filled. Review and save.',
          });
        },
        onError: (error) => {
          toast({
            title: 'AI analysis failed',
            description: error.message,
            variant: 'destructive',
          });
        },
      }
    );
  };

  const handleFilePicked = async (file: File | null) => {
    if (!file) return;
    if (file.size > MAX_FILE_BYTES) {
      toast({ title: 'File is too large', description: 'Max ~7MB', variant: 'destructive' });
      return;
    }
    setIsUploading(true);
    setProposal(null);
    try {
      const base64 = await readFileAsBase64(file);
      const uploaded = await uploadFile.mutateAsync({
        orderId,
        fileType: 'invoice',
        fileName: file.name,
        contentType: file.type || null,
        base64,
      });
      setFileId(uploaded.id);
      setFileName(uploaded.file_name);
      await utils.purchaseOrders.listPurchaseOrderFiles.invalidate({ orderId });
      const isPdf =
        file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
      if (!isEdit && aiEnabled && isPdf) {
        runAnalysis(uploaded.id);
      }
    } catch (error) {
      reportError('Failed to upload invoice file', error);
      toast({
        title: 'Failed to upload file',
        description: error instanceof Error ? error.message : undefined,
        variant: 'destructive',
      });
    } finally {
      setIsUploading(false);
    }
  };

  const addPoItemRow = (poItemId: string) => {
    const poItem = poItems.find((item) => item.id === poItemId);
    if (!poItem) return;
    setItems((prev) => [
      ...prev,
      {
        key: newKey(),
        itemId: null,
        purchaseOrderItemId: poItem.id,
        sku: poItem.sku ?? '',
        description: poItem.title ?? poItem.sku ?? '',
        quantity: String(poItem.ordered_quantity),
        unitCost: String(poItem.unit_cost ?? 0),
        taxRateId: null,
        taxRate: 0,
        accountCode: null,
      },
    ]);
  };

  const addFreehandRow = () => {
    setItems((prev) => [
      ...prev,
      {
        key: newKey(),
        itemId: null,
        purchaseOrderItemId: null,
        sku: '',
        description: '',
        quantity: '1',
        unitCost: '0',
        taxRateId: null,
        taxRate: 0,
        accountCode: null,
      },
    ]);
  };

  const patchRow = (key: string, patch: Partial<LineRow>) => {
    setItems((prev) => prev.map((row) => (row.key === key ? { ...row, ...patch } : row)));
  };

  const setRowTax = (key: string, taxRateId: string | null) => {
    const rate = taxRateId
      ? (taxRatesQuery.data?.find((r) => r.id === taxRateId)?.rate ?? 0)
      : 0;
    patchRow(key, { taxRateId, taxRate: rate });
  };

  const removeRow = (key: string) => {
    setItems((prev) => prev.filter((row) => row.key !== key));
  };

  const totals = useMemo(() => {
    let subtotal = 0;
    let tax = 0;
    for (const row of items) {
      const line = toNumber(row.quantity) * toNumber(row.unitCost);
      subtotal += line;
      tax += line * (row.taxRate ?? 0);
    }
    return { subtotal, tax, total: subtotal + tax };
  }, [items]);

  const rowToItemInput = (row: LineRow) => ({
    purchaseOrderItemId: row.purchaseOrderItemId,
    sku: row.sku.trim() || null,
    description: row.description.trim() || null,
    quantity: toNumber(row.quantity),
    unitCost: toNumber(row.unitCost),
    taxRateId: row.taxRateId,
    taxRate: row.taxRate,
    accountCode: row.accountCode,
  });

  const invalidRows = items.filter((row) => toNumber(row.quantity) <= 0);
  const canSave =
    invoiceNumber.trim().length > 0 && invalidRows.length === 0 && !isUploading && !isSaving;

  const handleSave = async () => {
    if (!canSave) {
      setInvoiceNumberTouched(true);
      return;
    }
    setIsSaving(true);
    try {
      if (!isEdit) {
        await createInvoice.mutateAsync({
          purchaseOrderId: orderId,
          invoiceNumber: invoiceNumber.trim(),
          invoiceDate: invoiceDate || null,
          dueDate: dueDate || null,
          paymentTerms: paymentTerms.trim() || null,
          isDeposit: depositMode,
          notes: notes.trim() || null,
          fileId,
          items: items.map(rowToItemInput),
        });
        toast({ title: 'Invoice added' });
      } else {
        await updateInvoice.mutateAsync({
          invoiceId: invoice.id,
          invoiceNumber: invoiceNumber.trim(),
          invoiceDate: invoiceDate || null,
          dueDate: dueDate || null,
          paymentTerms: paymentTerms.trim() || null,
          notes: notes.trim() || null,
          fileId,
        });
        // Diff line items against the seeded set.
        const keptIds = new Set(
          items.map((row) => row.itemId).filter((id): id is string => id !== null)
        );
        for (const initialId of initialItemIdsRef.current) {
          if (!keptIds.has(initialId)) {
            await deleteItem.mutateAsync({ itemId: initialId });
          }
        }
        for (const row of items) {
          if (row.itemId) {
            await updateItem.mutateAsync({
              itemId: row.itemId,
              sku: row.sku.trim() || null,
              description: row.description.trim() || null,
              quantity: toNumber(row.quantity),
              unitCost: toNumber(row.unitCost),
              taxRateId: row.taxRateId,
              taxRate: row.taxRate,
              accountCode: row.accountCode,
            });
          } else {
            await addItem.mutateAsync({ invoiceId: invoice.id, ...rowToItemInput(row) });
          }
        }
        toast({ title: 'Invoice updated' });
      }
      onSaved();
      onOpenChange(false);
    } catch (error) {
      toast({
        title: isEdit ? 'Failed to update invoice' : 'Failed to add invoice',
        description: error instanceof Error ? error.message : undefined,
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const showAnalyzeButton =
    !isEdit && aiEnabled && fileId !== null && !analyzeInvoice.isPending && !isUploading;
  const invoiceNumberError = invoiceNumberTouched && invoiceNumber.trim().length === 0;

  const title = isEdit
    ? `Edit invoice ${invoice.invoice_number}`
    : depositMode
      ? 'Add deposit invoice'
      : 'Add invoice';

  return (
    <Dialog open={open} onOpenChange={(next) => !isSaving && onOpenChange(next)}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {title}
            {depositMode && (
              <Badge variant="outline" className="text-[10px] font-semibold uppercase tracking-wide">
                Deposit
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Attachment — matches the real workflow: the PDF arrives first. */}
          <div className="rounded-md border border-dashed bg-muted/20 px-4 py-3">
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept="application/pdf,.pdf,.xlsx,.xls,.csv,image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
              onChange={(event) => {
                void handleFilePicked(event.target.files?.[0] ?? null);
                event.target.value = '';
              }}
            />
            {fileId ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <Paperclip className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden="true" />
                  <span className="truncate flex-1">{fileName ?? 'Attached invoice file'}</span>
                  {proposal && !analyzeInvoice.isPending && (
                    <span className="inline-flex items-center gap-1 text-xs text-emerald-700">
                      <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
                      AI pre-filled
                    </span>
                  )}
                  {showAnalyzeButton && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-xs gap-1.5"
                      onClick={() => fileId && runAnalysis(fileId)}
                    >
                      <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
                      {proposal ? 'Re-analyze' : 'Analyze with AI'}
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isUploading || analyzeInvoice.isPending}
                  >
                    Replace
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0"
                    aria-label="Remove attached file"
                    onClick={() => {
                      setFileId(null);
                      setFileName(null);
                      setProposal(null);
                    }}
                    disabled={isUploading || analyzeInvoice.isPending}
                  >
                    <X className="h-3.5 w-3.5" aria-hidden="true" />
                  </Button>
                </div>
                {analyzeInvoice.isPending && (
                  <div
                    className="flex items-center gap-3 rounded-md border border-threadheads-purple/30 bg-threadheads-purple/10 px-3 py-2.5"
                    role="status"
                  >
                    <Loader2
                      className="h-4 w-4 shrink-0 animate-spin text-threadheads-purple motion-reduce:animate-none"
                      aria-hidden="true"
                    />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-threadheads-purple">
                        Reading your invoice
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Extracting header and line items. Usually 5 to 15 seconds.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
                className="flex w-full items-center justify-center gap-2 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors motion-reduce:transition-none"
              >
                {isUploading ? (
                  <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />
                ) : (
                  <FileUp className="h-4 w-4" aria-hidden="true" />
                )}
                {isUploading ? 'Uploading file' : 'Attach the supplier invoice (PDF or image)'}
                {!isEdit && aiEnabled && !isUploading && (
                  <span className="inline-flex items-center gap-1 text-xs text-emerald-700">
                    <Sparkles className="h-3 w-3" aria-hidden="true" />
                    AI will pre-fill
                  </span>
                )}
              </button>
            )}
          </div>

          {/* Variance review list — only after a successful analysis. */}
          {proposal && proposal.variance_flags.length > 0 && (
            <div className="rounded-md border border-amber-300/80 bg-amber-50 p-3 space-y-1.5" role="alert">
              <div className="flex items-center gap-2 text-sm font-medium text-amber-900">
                <AlertTriangle className="h-4 w-4" aria-hidden="true" />
                Review before saving
              </div>
              <ul className="list-disc space-y-1 pl-6 text-xs text-amber-900/90">
                {proposal.variance_flags.map((flag, index) => (
                  <li key={index}>
                    {flag.item_index !== null ? `Line ${flag.item_index + 1}: ` : ''}
                    {flag.message}
                  </li>
                ))}
              </ul>
              {proposal.detected_total !== null && (
                <p className="pl-6 text-xs text-amber-900/70 tabular-nums">
                  PDF-stated total: {format(proposal.detected_total, proposal.currency ?? currency)}
                </p>
              )}
            </div>
          )}

          {/* Header fields */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="po-invoice-number">
                Invoice number<span className="text-destructive ml-1">*</span>
              </Label>
              <Input
                id="po-invoice-number"
                value={invoiceNumber}
                onChange={(event) => setInvoiceNumber(event.target.value)}
                onBlur={() => setInvoiceNumberTouched(true)}
                placeholder="Supplier's invoice number"
                aria-invalid={invoiceNumberError}
              />
              {invoiceNumberError && (
                <p className="text-sm text-destructive">Invoice number is required.</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="po-invoice-date">Invoice date</Label>
              <Input
                id="po-invoice-date"
                type="date"
                value={invoiceDate}
                onChange={(event) => setInvoiceDate(event.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="po-invoice-due-date">Due date</Label>
              <Input
                id="po-invoice-due-date"
                type="date"
                value={dueDate}
                onChange={(event) => {
                  setDueDate(event.target.value);
                  setDueDateTouched(true);
                }}
              />
              {paymentTermsDays !== null && !dueDateTouched && (
                <p className="text-xs text-muted-foreground">
                  Derived from invoice date plus {paymentTermsDays}-day terms
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="po-invoice-terms">Payment terms</Label>
              <Input
                id="po-invoice-terms"
                value={paymentTerms}
                onChange={(event) => setPaymentTerms(event.target.value)}
                placeholder="30 days"
              />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label htmlFor="po-invoice-notes">Notes</Label>
              <Input
                id="po-invoice-notes"
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
                placeholder="Optional notes"
              />
            </div>
          </div>

          {/* Line items */}
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <Label>Line items</Label>
              <div className="flex items-center gap-1.5">
                {poItems.length > 0 && (
                  <Select value="" onValueChange={addPoItemRow}>
                    <SelectTrigger className="h-8 w-[220px] text-xs">
                      <SelectValue placeholder="Add from PO item" />
                    </SelectTrigger>
                    <SelectContent>
                      {poItems.map((poItem) => (
                        <SelectItem key={poItem.id} value={poItem.id}>
                          {poItem.sku ?? poItem.title ?? 'PO item'} · {poItem.ordered_quantity} ordered
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 px-3 text-xs gap-1.5"
                  onClick={addFreehandRow}
                >
                  <Plus className="h-3.5 w-3.5" aria-hidden="true" />
                  Add line
                </Button>
              </div>
            </div>

            <div className="overflow-x-auto rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[110px]">SKU</TableHead>
                    <TableHead className="min-w-[220px]">Description</TableHead>
                    <TableHead className="w-[80px] text-right">Qty</TableHead>
                    <TableHead className="w-[110px] text-right">Unit cost</TableHead>
                    <TableHead className="w-[170px]">Account</TableHead>
                    <TableHead className="w-[150px]">Tax rate</TableHead>
                    <TableHead className="w-[110px] text-right">Total</TableHead>
                    <TableHead className="w-[44px]">
                      <span className="sr-only">Remove</span>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="py-6 text-center text-sm text-muted-foreground">
                        Add a PO item as a starter row, or add a freehand line.
                      </TableCell>
                    </TableRow>
                  ) : (
                    items.map((row) => (
                      <TableRow key={row.key}>
                        <TableCell className="py-1.5 px-3">
                          <Input
                            value={row.sku}
                            onChange={(event) => patchRow(row.key, { sku: event.target.value })}
                            className="h-8 font-mono text-sm"
                            placeholder="Optional"
                            aria-label="Line SKU"
                          />
                        </TableCell>
                        <TableCell className="py-1.5 px-3">
                          <Input
                            value={row.description}
                            onChange={(event) =>
                              patchRow(row.key, { description: event.target.value })
                            }
                            className="h-8 text-sm"
                            placeholder="Line description"
                            aria-label="Line description"
                          />
                        </TableCell>
                        <TableCell className="py-1.5 px-3">
                          <Input
                            inputMode="numeric"
                            value={row.quantity}
                            onChange={(event) => patchRow(row.key, { quantity: event.target.value })}
                            className="h-8 text-right tabular-nums"
                            aria-label="Quantity"
                          />
                        </TableCell>
                        <TableCell className="py-1.5 px-3">
                          <Input
                            inputMode="decimal"
                            value={row.unitCost}
                            onChange={(event) => patchRow(row.key, { unitCost: event.target.value })}
                            className="h-8 text-right tabular-nums"
                            aria-label="Unit cost"
                          />
                        </TableCell>
                        <TableCell className="py-1.5 px-3">
                          <Select
                            value={row.accountCode ?? 'none'}
                            onValueChange={(value) =>
                              patchRow(row.key, { accountCode: value === 'none' ? null : value })
                            }
                          >
                            <SelectTrigger className="h-8 text-xs" aria-label="Account code">
                              <SelectValue placeholder="Account" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">None</SelectItem>
                              {accounts.map((account) => (
                                <SelectItem key={account.id} value={account.account_code}>
                                  {account.account_code} · {account.name}
                                </SelectItem>
                              ))}
                              {/* AI-suggested code outside the curated dropdown still renders. */}
                              {row.accountCode &&
                                !accounts.some((account) => account.account_code === row.accountCode) && (
                                  <SelectItem value={row.accountCode}>{row.accountCode}</SelectItem>
                                )}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell className="py-1.5 px-3">
                          <Select
                            value={row.taxRateId ?? 'none'}
                            onValueChange={(value) =>
                              setRowTax(row.key, value === 'none' ? null : value)
                            }
                          >
                            <SelectTrigger className="h-8 text-xs" aria-label="Tax rate">
                              <SelectValue placeholder="Tax" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">None</SelectItem>
                              {expenseTaxRates.map((rate) => (
                                <SelectItem key={rate.id} value={rate.id}>
                                  {rate.name} ({Math.round(rate.rate * 100)}%)
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell className="py-1.5 px-3 text-right text-sm tabular-nums">
                          {format(toNumber(row.quantity) * toNumber(row.unitCost), currency)}
                        </TableCell>
                        <TableCell className="py-1.5 px-3">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0"
                            aria-label="Remove line"
                            onClick={() => removeRow(row.key)}
                          >
                            <Trash2 className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {invalidRows.length > 0 && (
              <p className="text-sm text-destructive">
                Every line needs a quantity greater than zero.
              </p>
            )}

            {items.length > 0 && (
              <div className="ml-auto w-56 space-y-1 text-sm">
                <div className="flex justify-between text-muted-foreground">
                  <span>Subtotal</span>
                  <span className="tabular-nums">{format(totals.subtotal, currency)}</span>
                </div>
                <div className="flex justify-between text-muted-foreground">
                  <span>Tax</span>
                  <span className="tabular-nums">{format(totals.tax, currency)}</span>
                </div>
                <div className="flex justify-between border-t pt-1 font-medium">
                  <span>Total</span>
                  <span className="tabular-nums">{format(totals.total, currency)}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            type="button"
            className="bg-threadheads-purple text-white hover:bg-threadheads-purple/90"
            onClick={() => void handleSave()}
            disabled={!canSave}
          >
            {isSaving && (
              <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin motion-reduce:animate-none" aria-hidden="true" />
            )}
            {isEdit ? 'Save changes' : 'Save invoice'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
